export type BillType = 'boleta' | 'factura';

/**
 * Which i18n key holds each fiscal label, per country.
 *
 * The wording itself lives in `public/locales/<lang>/booking.json` under
 * `<PREFIX>_<ISO>` — `PERSONAL_ID_NUMBER_CL`, `BUSINESS_BILL_PE`, and so on.
 * Keeping it there rather than in a table here is what makes it configurable:
 * the translation manifest is generated from those files, so every country's
 * labels show up in the admin's custom-texts editor with their real default,
 * and a venue's `appTexts` override lands on them through AppTextsInjector
 * without any code path of its own.
 *
 * A country with no entry falls through to the generic key, which reads
 * correctly anywhere.
 */
const LABEL_KEY_PREFIX = {
  personalReceipt: 'PERSONAL_BILL',
  businessReceipt: 'BUSINESS_BILL',
  personalDocument: 'PERSONAL_ID_NUMBER',
  businessDocument: 'BUSINESS_TAX_ID',
} as const;

const GENERIC_KEY = {
  personalReceipt: 'FISCAL_PERSONAL_GENERIC',
  businessReceipt: 'FISCAL_BUSINESS_GENERIC',
  personalDocument: 'FISCAL_DOCUMENT_GENERIC',
  businessDocument: 'FISCAL_TAX_ID_GENERIC',
} as const;

export type ReceiptLabelSlot = keyof typeof LABEL_KEY_PREFIX;

/**
 * The four bare legacy names — the pre-existing appTexts override surface.
 * Consumers that special-case the venue-override entry of a fallback list
 * (useBillingReceipt's gate) match against THIS set rather than assuming a
 * position, so a reorder here cannot silently make them act on a country key.
 */
export const LEGACY_OVERRIDE_KEYS: ReadonlySet<string> = new Set(Object.values(LABEL_KEY_PREFIX));

/**
 * i18next key-fallback lists: the venue-override key first, then the country's
 * key, then the generic one behind it.
 */
export type ReceiptLabelKeys = Record<ReceiptLabelSlot, string[]>;

/**
 * Why each country's labels read the way they do. This is researched tax law,
 * not wording preference — check here before "correcting" one of them.
 *
 * - PE — SUNAT: boleta de venta (persona) / factura (empresa).
 * - CL — SII: boleta / factura. The RUT identifies people and companies alike,
 *   so the two document labels say whose RUT it is; a guest asking for a boleta
 *   may be a tourist with only a passport.
 * - AR — ARCA/AFIP: the letter (A/B/C) follows the issuing venue's own tax
 *   standing, not the guest's. A monotributista venue only ever issues C. Meitre
 *   does not know which the venue is and the guest has no say, so the labels ask
 *   the one thing the guest does decide: whose name the invoice carries.
 * - BR — NFC-e and NF-e are the venue's document types, not what a guest asks
 *   for: a diner asks for the nota "com CPF", and adding the CPF does not even
 *   change the document type. NF-e only enters when it goes to a company's CNPJ.
 * - MX — SAT/CFDI 4.0: an RFC belongs to individuals too, so the split is who
 *   the invoice is addressed to rather than person vs company. Both sides
 *   collect an RFC. An explicit "no invoice" option was considered and dropped:
 *   unrequested consumption rolls into the venue's global invoice anyway.
 * - UY — DGI calls these e-Ticket (CFE 101) and e-Factura (CFE 111), but nobody
 *   asks a waiter for an "e-Ticket" — you ask for a factura, and say "con RUT"
 *   when it is for a company.
 * - CO — DIAN: asking for the invoice with a NIT is what obliges the venue to
 *   issue a full electronic invoice instead of the POS equivalent.
 * - PY — DNIT/SIFEN: there is no "boleta de venta" here, unlike Peru and Chile.
 *   The only boletas in the decree are RESIMPLE (Régimen Simplificado
 *   micro-taxpayers, which a restaurant is not) and public-event entry tickets.
 * - EC — SRI: the nota de venta belongs to a tax regime, not to a kind of buyer.
 *   Resolution NAC-DGERCGC24-00000027 art. 6 authorises it "únicamente" for
 *   RIMPE negocios populares (under USD 20k a year). A venue taking deposits
 *   issues a factura to a person and a company alike.
 * - BO — SIN: both are "factura"; only the buyer's NIT differs, so the labels
 *   say so rather than showing the same word on both buttons.
 * - GT — SAT/FEL: decreto 31-2024 removed the anonymous consumer invoice, so a
 *   NIT is always requested; the personal case keeps "consumidor final".
 * - CR — Hacienda: asking for the invoice with a cédula is what obliges the
 *   venue to issue a factura electrónica instead of a tiquete.
 * - DO — DGII: NCF type 32 (consumo) / type 31 (crédito fiscal).
 * - SV — Hacienda: the naming is inverted against PE/CL — "factura" is the
 *   PERSONAL document here, and the company one is the CCF. Do not "fix" this
 *   by analogy.
 *
 * Document names are legal identifiers, so they stay in the local term in every
 * language: a guest booking in Chile reads "RUT" whatever language the widget
 * is in, because that is what their ID card says. Only the qualifier around it
 * gets translated.
 */
export function receiptLabelKeys(country?: string | null): ReceiptLabelKeys {
  // The bare legacy key (PERSONAL_BILL, BUSINESS_BILL, PERSONAL_ID_NUMBER,
  // BUSINESS_TAX_ID) goes FIRST. Those four names are the pre-existing
  // appTexts override surface — the exact keys the legacy widget exposed and
  // a venue can already carry. They no longer exist in the static locale
  // files, so the bare key resolves ONLY when AppTextsInjector added a
  // venue's override to the booking bundle — meaning it wins exactly when an
  // override exists and is transparent otherwise. Dropping it from this list
  // silently reverts a customized venue to the country default.
  const keysFor = (slot: ReceiptLabelSlot) =>
    country
      ? [LABEL_KEY_PREFIX[slot], `${LABEL_KEY_PREFIX[slot]}_${country}`, GENERIC_KEY[slot]]
      : [LABEL_KEY_PREFIX[slot], GENERIC_KEY[slot]];

  return {
    personalReceipt: keysFor('personalReceipt'),
    businessReceipt: keysFor('businessReceipt'),
    personalDocument: keysFor('personalDocument'),
    businessDocument: keysFor('businessDocument'),
  };
}

/**
 * The value sent to the backend as `billType`, for every country. It means
 * person vs company, is not display text, and is never renamed to match a local
 * receipt name — the backend flattens it into a free-text billing_notes column,
 * so a country-specific value would leave that column holding fourteen
 * different vocabularies with no way to group them afterwards.
 */
export const BILL_TYPE_CONTRACT: Record<'personal' | 'business', BillType> = {
  personal: 'boleta',
  business: 'factura',
};
